import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  FaEnvelope, FaWhatsapp, FaGlobe, FaMapMarkerAlt,
  FaFacebookF, FaTwitter, FaLinkedinIn,
  FaPaperPlane, FaCheckCircle
} from 'react-icons/fa';
import './ContactUs.css';

const contactInfo = [
  {
    icon: <FaEnvelope />,
    title: 'Email Us',
    value: 'info@techmascot.in',
    sub: 'We respond within 24 hours',
    href: 'mailto:info@techmascot.in',
  },
  {
    icon: <FaWhatsapp />,
    title: 'WhatsApp',
    value: 'Chat With Us',
    sub: 'Quick support via WhatsApp',
    href: 'https://wa.me/917903567473',
  },
  {
    icon: <FaGlobe />,
    title: 'Website',
    value: 'www.techmascot.in',
    sub: 'Explore our solutions',
    href: 'https://www.techmascot.in',
  },
  {
    icon: <FaMapMarkerAlt />,
    title: 'Location',
    value: 'India',
    sub: 'Serving clients globally',
    href: null,
  },
];

const socialLinks = [
  { icon: <FaFacebookF />, label: 'Facebook', href: 'https://facebook.com', className: 'fb' },
  { icon: <FaTwitter />, label: 'Twitter', href: 'https://twitter.com', className: 'tw' },
  { icon: <FaLinkedinIn />, label: 'LinkedIn', href: 'https://linkedin.com', className: 'li' },
  { icon: <FaWhatsapp />, label: 'WhatsApp', href: 'https://wa.me/917903567473', className: 'wa' },
];

const services = [
  'Payment Gateway',
  'Escrow Payment System',
  'Reconciliation Engine',
  'Finance Application',
  'Cloud Services',
  'API Integration',
  'Security Consulting',
  'Other',
];

function ContactUs() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="contact-page">
      <Helmet>
        <title>Contact Us | Techmascot Technologies</title>
        <meta name="description" content="Get in touch with Techmascot Technologies for fintech software solutions, partnerships and technical support. We respond within 24 hours." />
        <link rel="canonical" href="https://www.techmascot.com/contact" />
        <meta property="og:title" content="Contact Us | Techmascot Technologies" />
        <meta property="og:description" content="Get in touch with Techmascot Technologies for fintech software solutions, partnerships and technical support." />
        <meta property="og:url" content="https://www.techmascot.com/contact" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Techmascot Technologies Private Limited",
            "url": "https://www.techmascot.com",
            "description": "Leading Fintech Software Development Company",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "IN"
            }
          }
        `}</script>
      </Helmet>
      {/* Banner */}
      <section className="page-banner">
        <div className="banner-bg">
          <div className="banner-grid"></div>
        </div>
        <div className="container">
          <div className="banner-content">
            <span className="tag">Get In Touch</span>
            <h1>Contact <span>Us</span></h1>
            <p>
              Ready to build your fintech solution? Let's talk. Our team is here
              to answer your questions and kickstart your project.
            </p>
          </div>
        </div>
        <div className="banner-wave">
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none">
            <path d="M0,30 C480,60 960,0 1440,30 L1440,60 L0,60 Z" fill="#F8FAFF" />
          </svg>
        </div>
      </section>

      {/* Contact Main */}
      <section className="contact-main">
        <div className="container">
          <div className="contact-layout">
            {/* Contact Info */}
            <div className="contact-info-col">
              <h2>Let's Start a Conversation</h2>
              <p>
                Whether you have a specific project in mind or just want to explore possibilities,
                we'd love to hear from you. Fill in the form or reach us directly through
                any of the channels below.
              </p>

              <div className="contact-cards">
                {contactInfo.map((c, i) => (
                  <div key={i} className="contact-info-card">
                    <div className="ci-icon">{c.icon}</div>
                    <div className="ci-text">
                      <div className="ci-title">{c.title}</div>
                      {c.href ? (
                        <a href={c.href} target={c.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="ci-value">
                          {c.value}
                        </a>
                      ) : (
                        <div className="ci-value">{c.value}</div>
                      )}
                      <div className="ci-sub">{c.sub}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Social */}
              <div className="contact-social">
                <h4>Follow Us</h4>
                <div className="social-row">
                  {socialLinks.map((s, i) => (
                    <a
                      key={i}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`contact-social-btn ${s.className}`}
                      aria-label={s.label}
                    >
                      {s.icon}
                      <span>{s.label}</span>
                    </a>
                  ))}
                </div>
              </div>

              {/* Response time */}
              <div className="response-info">
                <FaCheckCircle />
                <div>
                  <strong>Fast Response Guaranteed</strong>
                  <p>We respond to all inquiries within 24 business hours.</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-col">
              {submitted ? (
                <div className="success-message">
                  <div className="success-icon"><FaCheckCircle /></div>
                  <h3>Thank You!</h3>
                  <p>
                    Your message has been received. Our team will get back to you at
                    <strong> {form.email}</strong> within 24 hours.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name:'', email:'', phone:'', company:'', service:'', message:'' }); }}
                    className="btn-primary"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form className="contact-form" onSubmit={handleSubmit}>
                  <h3>Send Us a Message</h3>
                  <p className="form-sub">Fill out the form below and we'll get back to you shortly.</p>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Full Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email Address *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">Phone Number</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+91 XXXXX XXXXX"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="company">Company Name</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        placeholder="Your company"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="service">Service Interested In</label>
                    <select id="service" name="service" value={form.service} onChange={handleChange}>
                      <option value="">Select a service...</option>
                      {services.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Your Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project, requirements, or any questions you have..."
                      rows={5}
                      required
                    />
                  </div>

                  <button type="submit" className="form-submit-btn">
                    <FaPaperPlane />
                    Send Message
                  </button>

                  <p className="form-note">
                    By submitting this form, you agree to our Privacy Policy.
                    We'll never share your information with third parties.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact Bar */}
      <section className="quick-contact">
        <div className="container">
          <div className="qc-inner">
            <div className="qc-text">
              <h3>Prefer a Direct Line?</h3>
              <p>Reach us instantly via email or WhatsApp for urgent queries.</p>
            </div>
            <div className="qc-actions">
              <a href="mailto:info@techmascot.in" className="qc-btn email">
                <FaEnvelope />
                info@techmascot.in
              </a>
              <a href="https://wa.me/917903567473" target="_blank" rel="noopener noreferrer" className="qc-btn whatsapp">
                <FaWhatsapp />
                Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ContactUs;
